import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { isRecord } from "../data/map";
import {
  loadForms,
  loadSubmissions,
  onRemoteSession,
  remoteClient,
  saveForm,
  saveSubmission,
  subscribeChanges,
  type RemoteForm,
  type RemoteSubmission,
} from "../data/remote";
import { ageForGrade, students as seedStudents, type School } from "../domain/data";
import { omitStudentId } from "../domain/roster";
import {
  consentFlags,
  emptyPermission,
  normalizePermission,
  PERMISSION_FORM_INTRO,
  PERMISSION_FORM_INTRO_ES,
  PERMISSION_FORM_NAME,
  type PermissionAnswers,
} from "../domain/permissionForm";
import {
  cloneQuestions,
  mergeQuestionTranslation,
  normalizeQuestion,
  questionLabel,
  type FormQuestion,
} from "../domain/questions";

export type ExtraQuestion = FormQuestion;

const STORAGE_KEY = "lift-app-forms-v3";

export type FormStatus = "open" | "archived";

export type ConsentId = "permissionSlips" | "medical" | "conduct";

export type FormConsent = {
  id: ConsentId;
  label: string;
};


export type FormCopy = {
  intro: string;
  studentHeading: string;
  guardianHeading: string;
  emergencyHeading: string;
  permissionsHeading: string;
  labels: {
    given: string;
    family: string;
    email: string;
    school: string;
    grade: string;
    birthday: string;
    guardianName: string;
    guardianEmail: string;
    guardianPhone: string;
    emergencyName: string;
    emergencyPhone: string;
  };
  consents: FormConsent[];
  extraQuestions: ExtraQuestion[];
};

export type SchoolYearForm = {
  id: string;
  year: string;
  name: string;
  status: FormStatus;
  openedLabel: string;
  archivedLabel: string | null;
  copy: FormCopy;
};

export type FormAnswers = {
  given: string;
  family: string;
  email: string;
  school: School;
  grade: number;
  age: number;
  birthday: string;
  guardianName: string;
  guardianEmail: string;
  guardianPhone: string;
  emergencyName: string;
  emergencyPhone: string;
  permissionSlips: boolean;
  medical: boolean;
  conduct: boolean;
  permission: PermissionAnswers;
  extraAnswers: Record<string, string>;
};

export type FormSubmission = {
  id: string;
  formId: string;
  studentId: string;
  submittedAt: string;
  submittedLabel: string;
  answers: FormAnswers;
};

type Stored = {
  forms: SchoolYearForm[];
  submissions: FormSubmission[];
};

type FormsContextValue = {
  forms: SchoolYearForm[];
  submissions: FormSubmission[];
  currentForm: SchoolYearForm | undefined;
  getForm: (id: string) => SchoolYearForm | undefined;
  submissionsFor: (formId: string) => FormSubmission[];
  submissionForStudent: (
    formId: string,
    studentId: string,
  ) => FormSubmission | undefined;
  submissionsForStudent: (studentId: string) => FormSubmission[];
  submitForm: (formId: string, studentId: string, answers: FormAnswers) => Promise<void>;
  replaceAnswers: (formId: string, studentId: string, answers: FormAnswers) => Promise<void>;
  forgetStudent: (studentId: string) => Promise<void>;
  importSubmissions: (
    rows: Array<{
      formId: string;
      studentId: string;
      answers: FormAnswers;
    }>,
  ) => Promise<void>;
  addForm: (year: string) => SchoolYearForm | { error: string };
  updateForm: (
    formId: string,
    patch: { name?: string; copy?: FormCopy },
  ) => void;
};

const FormsContext = createContext<FormsContextValue | null>(null);

export function defaultFormCopy(): FormCopy {
  return {
    intro: PERMISSION_FORM_INTRO,
    studentHeading: "Student",
    guardianHeading: "Guardian",
    emergencyHeading: "Emergency contact",
    permissionsHeading: "This year's permissions",
    labels: {
      given: "First name",
      family: "Last name",
      email: "Student email",
      school: "School",
      grade: "Grade",
      birthday: "Birthday",
      guardianName: "Name",
      guardianEmail: "Email",
      guardianPhone: "Phone",
      emergencyName: "Name",
      emergencyPhone: "Phone",
    },
    consents: [
      { id: "permissionSlips", label: "Permission slips and waivers signed" },
      { id: "medical", label: "Emergency medical treatment authorization" },
      { id: "conduct", label: "Code of conduct acknowledgment" },
    ],
    extraQuestions: [],
  };
}

export function defaultFormCopyEs(): FormCopy {
  return {
    intro: PERMISSION_FORM_INTRO_ES,
    studentHeading: "Estudiante",
    guardianHeading: "Tutor",
    emergencyHeading: "Contacto de emergencia",
    permissionsHeading: "Permisos de este año",
    labels: {
      given: "Nombre",
      family: "Apellido",
      email: "Correo del estudiante",
      school: "Escuela",
      grade: "Grado",
      birthday: "Cumpleaños",
      guardianName: "Nombre",
      guardianEmail: "Correo",
      guardianPhone: "Teléfono",
      emergencyName: "Nombre",
      emergencyPhone: "Teléfono",
    },
    consents: [
      {
        id: "permissionSlips",
        label: "Permisos y exenciones firmados",
      },
      {
        id: "medical",
        label: "Autorización de tratamiento médico de emergencia",
      },
      {
        id: "conduct",
        label: "Reconocimiento del código de conducta",
      },
    ],
    extraQuestions: [],
  };
}

const RETIRED_FORM_INTROS = new Set([
  "Fill this year's Lift form once. After you submit, sign in to see events for your school.",
  "This is the LIFT permission form for 2026-2027 programming. Fill it once for each student. After you submit, sign in to see events for your school.",
]);

/** Stored forms that still tell a guest to sign in after submit use the current intro. */
export function storedFormIntro(stored: string | undefined, current: string): string {
  if (!stored || RETIRED_FORM_INTROS.has(stored)) return current;
  return stored;
}

export function formCopyForLocale(
  copy: FormCopy,
  locale: "en" | "es",
): FormCopy {
  if (locale !== "es") return copy;
  const english = defaultFormCopy();
  const spanish = defaultFormCopyEs();
  const labelKey = <K extends keyof FormCopy["labels"]>(key: K) =>
    copy.labels[key] === english.labels[key]
      ? spanish.labels[key]
      : copy.labels[key];
  return {
    intro: copy.intro === english.intro ? spanish.intro : copy.intro,
    studentHeading:
      copy.studentHeading === english.studentHeading
        ? spanish.studentHeading
        : copy.studentHeading,
    guardianHeading:
      copy.guardianHeading === english.guardianHeading
        ? spanish.guardianHeading
        : copy.guardianHeading,
    emergencyHeading:
      copy.emergencyHeading === english.emergencyHeading
        ? spanish.emergencyHeading
        : copy.emergencyHeading,
    permissionsHeading:
      copy.permissionsHeading === english.permissionsHeading
        ? spanish.permissionsHeading
        : copy.permissionsHeading,
    labels: {
      given: labelKey("given"),
      family: labelKey("family"),
      email: labelKey("email"),
      school: labelKey("school"),
      grade: labelKey("grade"),
      birthday: labelKey("birthday"),
      guardianName: labelKey("guardianName"),
      guardianEmail: labelKey("guardianEmail"),
      guardianPhone: labelKey("guardianPhone"),
      emergencyName: labelKey("emergencyName"),
      emergencyPhone: labelKey("emergencyPhone"),
    },
    consents: copy.consents.map((item) => {
      const fallback = english.consents.find((row) => row.id === item.id);
      const translated = spanish.consents.find((row) => row.id === item.id);
      if (fallback && translated && item.label === fallback.label) {
        return { ...item, label: translated.label };
      }
      return item;
    }),
    extraQuestions: copy.extraQuestions.map((item) => ({
      ...item,
      label: questionLabel(item, locale),
    })),
  };
}

function priorYearCopy(): FormCopy {
  return {
    ...defaultFormCopy(),
    intro: "Complete the Lift school-year form for 2025-2026.",
    permissionsHeading: "Permissions and waivers",
    consents: [
      { id: "permissionSlips", label: "Permission slips signed" },
      { id: "medical", label: "Medical treatment authorization" },
      { id: "conduct", label: "Student code of conduct" },
    ],
    extraQuestions: [],
  };
}

export function cloneFormCopy(copy: FormCopy): FormCopy {
  return {
    ...copy,
    labels: { ...copy.labels },
    consents: copy.consents.map((item) => ({ ...item })),
    extraQuestions: cloneQuestions(copy.extraQuestions),
  };
}

function answersFromSeed(
  student: (typeof seedStudents)[number],
  grade: number,
  extraAnswers: Record<string, string> = {},
): FormAnswers {
  return {
    given: student.given,
    family: student.family,
    email: student.email,
    school: student.school,
    grade,
    age: ageForGrade(grade),
    birthday: student.birthday,
    guardianName: student.guardianName,
    guardianEmail: student.guardianEmail,
    guardianPhone: student.guardianPhone,
    emergencyName: student.emergencyName,
    emergencyPhone: student.emergencyPhone,
    permissionSlips: true,
    medical: true,
    conduct: true,
    permission: emptyPermission(),
    extraAnswers,
  };
}

function cloneSeed(): Stored {
  const prior: SchoolYearForm = {
    id: "sy-2025-2026",
    year: "2025-2026",
    name: "Lift 2025-2026 School Year",
    status: "archived",
    openedLabel: "Aug 12, 2025",
    archivedLabel: "Jun 12, 2026",
    copy: priorYearCopy(),
  };
  const current: SchoolYearForm = {
    id: "sy-2026-2027",
    year: "2026-2027",
    name: PERMISSION_FORM_NAME,
    status: "open",
    openedLabel: "Aug 10, 2026",
    archivedLabel: null,
    copy: defaultFormCopy(),
  };

  const priorIds = new Set(["mike", "sue", "tom", "dan", "bill"]);
  const submissions: FormSubmission[] = [];

  seedStudents.forEach((student, index) => {
    if (priorIds.has(student.id)) {
      const submittedAt = seedIsoDate(2025, 7, 12 + (index % 18));
      submissions.push({
        id: `${prior.id}-${student.id}`,
        formId: prior.id,
        studentId: student.id,
        submittedAt,
        submittedLabel: labelFromIso(submittedAt),
        answers: answersFromSeed(student, Math.max(6, student.grade - 1)),
      });
    }
    const submittedAt = seedIsoDate(2026, index < 16 ? 7 : 8, 10 + (index % 16));
    submissions.push({
      id: `${current.id}-${student.id}`,
      formId: current.id,
      studentId: student.id,
      submittedAt,
      submittedLabel: labelFromIso(submittedAt),
      answers: answersFromSeed(student, student.grade),
    });
  });

  return { forms: [current, prior], submissions };
}

function readStore(): Stored {
  if (typeof window === "undefined") return cloneSeed();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return cloneSeed();
    const parsed = JSON.parse(raw) as Stored;
    if (!parsed || !Array.isArray(parsed.forms)) return cloneSeed();
    return {
      forms: parsed.forms.map(normalizeForm),
      submissions: (parsed.submissions ?? []).map(normalizeSubmission),
    };
  } catch {
    return cloneSeed();
  }
}

function normalizeForm(form: SchoolYearForm): SchoolYearForm {
  const defaults = defaultFormCopy();
  const copy = form.copy ?? defaults;
  return {
    ...form,
    copy: {
      ...defaults,
      ...copy,
      intro: storedFormIntro(copy.intro, defaults.intro),
      labels: { ...defaults.labels, ...copy.labels },
      extraQuestions: (copy.extraQuestions ?? [])
        .filter((item) => item.id !== "q-tshirt")
        .map((item) => {
        const normalized = normalizeQuestion(item);
        const seed = defaults.extraQuestions.find(
          (row) => row.id === normalized.id,
        );
        return mergeQuestionTranslation(normalized, seed);
      }),
    },
  };
}

function normalizeSubmission(row: FormSubmission): FormSubmission {
  return {
    ...row,
    answers: {
      ...row.answers,
      birthday: row.answers.birthday ?? "",
      permission: normalizePermission(row.answers.permission),
      extraAnswers: row.answers.extraAnswers ?? {},
    },
  };
}

function persist(store: Stored) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* ignore quota / private mode */
  }
}

function yearId(year: string): string {
  const slug =
    year
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "year";
  return `sy-${slug}`;
}

function todayIso(): string {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function todayLabel(): string {
  return labelFromIso(todayIso());
}

function seedIsoDate(year: number, monthIndex: number, day: number): string {
  const date = new Date(year, monthIndex, day);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function labelFromIso(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return iso;
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function submissionDate(row: FormSubmission): string {
  return row.submittedAt || "";
}

function isFormCopy(value: unknown): value is FormCopy {
  return isRecord(value) && typeof value.intro === "string" && isRecord(value.labels);
}

function answersFromUnknown(value: unknown): FormAnswers | null {
  if (!isRecord(value) || typeof value.given !== "string" || typeof value.family !== "string") {
    return null;
  }
  if (typeof value.email !== "string" || typeof value.school !== "string") return null;
  const school = value.school;
  if (
    school !== "Suttons Bay" &&
    school !== "Northport" &&
    school !== "Leland" &&
    school !== "Glen Lake"
  ) {
    return null;
  }
  return {
    given: value.given,
    family: value.family,
    email: value.email,
    school,
    grade: typeof value.grade === "number" ? value.grade : 9,
    age: typeof value.age === "number" ? value.age : 14,
    birthday: typeof value.birthday === "string" ? value.birthday : "",
    guardianName: typeof value.guardianName === "string" ? value.guardianName : "",
    guardianEmail: typeof value.guardianEmail === "string" ? value.guardianEmail : "",
    guardianPhone: typeof value.guardianPhone === "string" ? value.guardianPhone : "",
    emergencyName: typeof value.emergencyName === "string" ? value.emergencyName : "",
    emergencyPhone: typeof value.emergencyPhone === "string" ? value.emergencyPhone : "",
    permissionSlips: value.permissionSlips === true,
    medical: value.medical === true,
    conduct: value.conduct === true,
    permission: normalizePermission(value.permission),
    extraAnswers: isRecord(value.extraAnswers)
      ? Object.fromEntries(
          Object.entries(value.extraAnswers).flatMap(([key, item]) =>
            typeof item === "string" ? [[key, item]] : [],
          ),
        )
      : {},
  };
}

function formFromRemote(row: RemoteForm): SchoolYearForm {
  const opened = row.createdAt.slice(0, 10);
  const updated = row.updatedAt.slice(0, 10);
  return normalizeForm({
    id: row.id,
    year: row.year,
    name: row.name,
    status: row.status,
    openedLabel: labelFromIso(opened),
    archivedLabel: row.status === "archived" ? labelFromIso(updated) : null,
    copy: isFormCopy(row.copy) ? row.copy : defaultFormCopy(),
  });
}

function submissionFromRemote(row: RemoteSubmission): FormSubmission | null {
  const answers = answersFromUnknown(row.answers);
  if (!answers) return null;
  const submittedAt = row.submittedAt.slice(0, 10);
  return normalizeSubmission({
    id: row.id,
    formId: row.formId,
    studentId: row.studentId,
    submittedAt,
    submittedLabel: labelFromIso(submittedAt),
    answers,
  });
}

function emptyForms(): Stored {
  return { forms: [], submissions: [] };
}

export function FormsProvider({ children }: { children: ReactNode }) {
  const [store, setStore] = useState<Stored>(() =>
    remoteClient() ? emptyForms() : readStore(),
  );
  const storeRef = useRef(store);
  storeRef.current = store;
  const loadGen = useRef(0);

  const write = useCallback((next: Stored) => {
    storeRef.current = next;
    setStore(next);
    if (!remoteClient()) persist(next);
  }, []);

  const reload = useCallback(async () => {
    const client = remoteClient();
    if (!client) return;
    const token = ++loadGen.current;
    try {
      const [formsResult, submissionsResult] = await Promise.allSettled([
        loadForms(client),
        loadSubmissions(client),
      ]);
      if (token !== loadGen.current) return;
      if (formsResult.status === "rejected") return;
      const next = {
        forms: formsResult.value.map(formFromRemote),
        submissions:
          submissionsResult.status === "fulfilled"
            ? submissionsResult.value.flatMap((row) => {
                const submission = submissionFromRemote(row);
                return submission ? [submission] : [];
              })
            : storeRef.current.submissions,
      };
      storeRef.current = next;
      setStore(next);
    } catch {
      /* keep the last snapshot */
    }
  }, []);

  useEffect(() => {
    const client = remoteClient();
    if (!client) return;
    const stopSession = onRemoteSession(client, () => {
      void reload();
    });
    const stop = subscribeChanges(
      client,
      ["school_year_forms", "form_submissions"],
      () => {
        void reload();
      },
    );
    const onFocus = () => {
      if (document.visibilityState === "visible") void reload();
    };
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      stopSession();
      stop();
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [reload]);

  const submitForm = useCallback(
    async (formId: string, studentId: string, answers: FormAnswers) => {
      const current = storeRef.current;
      const existing = current.submissions.find(
        (row) => row.formId === formId && row.studentId === studentId,
      );
      const submittedAt = todayIso();
      const next: FormSubmission = {
        id:
          existing?.id ??
          (remoteClient()
            ? crypto.randomUUID()
            : `${formId}-${studentId}-${Date.now().toString(36)}`),
        formId,
        studentId,
        submittedAt,
        submittedLabel: labelFromIso(submittedAt),
        answers,
      };
      write({
        ...current,
        submissions: existing
          ? current.submissions.map((row) => (row.id === existing.id ? next : row))
          : [next, ...current.submissions],
      });
      const client = remoteClient();
      if (client) {
        try {
          await saveSubmission(client, {
            id: next.id,
            formId,
            studentId,
            submittedAt,
            answers,
          });
          await reload();
        } catch (error) {
          await reload();
          throw error instanceof Error ? error : new Error("Could not save that form.");
        }
      }
    },
    [reload, write],
  );

  const replaceAnswers = useCallback(
    async (formId: string, studentId: string, answers: FormAnswers) => {
      const current = storeRef.current;
      const existing = current.submissions.find(
        (row) => row.formId === formId && row.studentId === studentId,
      );
      const submittedAt = existing?.submittedAt ?? todayIso();
      const next: FormSubmission = {
        id:
          existing?.id ??
          (remoteClient()
            ? crypto.randomUUID()
            : `${formId}-${studentId}-${Date.now().toString(36)}`),
        formId,
        studentId,
        submittedAt,
        submittedLabel: existing?.submittedLabel ?? labelFromIso(submittedAt),
        answers,
      };
      write({
        ...current,
        submissions: existing
          ? current.submissions.map((row) => (row.id === existing.id ? next : row))
          : [next, ...current.submissions],
      });
      const client = remoteClient();
      if (!client) return;
      try {
        await saveSubmission(client, {
          id: next.id,
          formId,
          studentId,
          submittedAt,
          answers,
        });
        await reload();
      } catch (error) {
        await reload();
        throw error instanceof Error ? error : new Error("Could not save that form.");
      }
    },
    [reload, write],
  );

  const forgetStudent = useCallback(
    async (studentId: string) => {
      const current = storeRef.current;
      write({
        ...current,
        submissions: omitStudentId(current.submissions, studentId),
      });
      if (remoteClient()) await reload();
    },
    [reload, write],
  );

  const importSubmissions = useCallback(
    async (
      rows: Array<{
        formId: string;
        studentId: string;
        answers: FormAnswers;
      }>,
    ) => {
      let submissions = storeRef.current.submissions;
      const submittedAt = todayIso();
      rows.forEach((row) => {
        const existing = submissions.find(
          (item) => item.formId === row.formId && item.studentId === row.studentId,
        );
        const next: FormSubmission = {
          id:
            existing?.id ??
            (remoteClient()
              ? crypto.randomUUID()
              : `${row.formId}-${row.studentId}-${Date.now().toString(36)}`),
          formId: row.formId,
          studentId: row.studentId,
          submittedAt: existing?.submittedAt ?? submittedAt,
          submittedLabel: existing?.submittedLabel ?? labelFromIso(submittedAt),
          answers: row.answers,
        };
        submissions = existing
          ? submissions.map((item) => (item.id === existing.id ? next : item))
          : [next, ...submissions];
      });
      write({ ...storeRef.current, submissions });
      const client = remoteClient();
      if (client) {
        const saved = submissions.filter((item) =>
          rows.some(
            (row) => row.formId === item.formId && row.studentId === item.studentId,
          ),
        );
        try {
          for (const item of saved) {
            await saveSubmission(client, {
              id: item.id,
              formId: item.formId,
              studentId: item.studentId,
              submittedAt: item.submittedAt,
              answers: item.answers,
            });
          }
          await reload();
        } catch {
          await reload();
          throw new Error("Could not save those forms.");
        }
      }
    },
    [reload, write],
  );

  const addForm = useCallback(
    (year: string) => {
      const trimmed = year.trim();
      if (!/^\d{4}\s*[-–]\s*\d{4}$/.test(trimmed)) {
        return { error: "Use a school year like 2027-2028." };
      }
      const normalized = trimmed.replace(/\s*[-–]\s*/, "-");
      if (store.forms.some((form) => form.year === normalized)) {
        return { error: `${normalized} is already saved.` };
      }
      const previous = store.forms.find((form) => form.status === "open");
      const created: SchoolYearForm = {
        id: remoteClient() ? crypto.randomUUID() : yearId(normalized),
        year: normalized,
        name: `LIFT Permission Form ${normalized}`,
        status: "open",
        openedLabel: todayLabel(),
        archivedLabel: null,
        copy: previous ? cloneFormCopy(previous.copy) : defaultFormCopy(),
      };
      write({
        ...store,
        forms: [
          created,
          ...store.forms.map((form) =>
            form.status === "open"
              ? { ...form, status: "archived" as const, archivedLabel: todayLabel() }
              : form,
          ),
        ],
      });
      const client = remoteClient();
      if (client) {
        const archived = store.forms.filter((form) => form.status === "open");
        void Promise.all([
          saveForm(client, {
            id: created.id,
            year: created.year,
            name: created.name,
            status: created.status,
            copy: created.copy,
          }),
          ...archived.map((form) =>
            saveForm(client, {
              id: form.id,
              year: form.year,
              name: form.name,
              status: "archived",
              copy: form.copy,
            }),
          ),
        ])
          .then(() => reload())
          .catch(() => reload());
      }
      return created;
    },
    [reload, store, write],
  );

  const updateForm = useCallback(
    (formId: string, patch: { name?: string; copy?: FormCopy }) => {
      write({
        ...store,
        forms: store.forms.map((form) =>
          form.id === formId
            ? {
                ...form,
                name: patch.name ?? form.name,
                copy: patch.copy ?? form.copy,
              }
            : form,
        ),
      });
      const client = remoteClient();
      const nextForm = store.forms.find((form) => form.id === formId);
      if (client && nextForm) {
        void saveForm(client, {
          id: formId,
          year: nextForm.year,
          name: patch.name ?? nextForm.name,
          status: nextForm.status,
          copy: patch.copy ?? nextForm.copy,
        })
          .then(() => reload())
          .catch(() => reload());
      }
    },
    [reload, store, write],
  );

  const value = useMemo<FormsContextValue>(
    () => ({
      forms: store.forms,
      submissions: store.submissions,
      currentForm: store.forms.find((form) => form.status === "open"),
      getForm: (id) => store.forms.find((form) => form.id === id),
      submissionsFor: (formId) =>
        store.submissions.filter((row) => row.formId === formId),
      submissionForStudent: (formId, studentId) =>
        store.submissions.find(
          (row) => row.formId === formId && row.studentId === studentId,
        ),
      submissionsForStudent: (studentId) =>
        store.submissions.filter((row) => row.studentId === studentId),
      submitForm,
      replaceAnswers,
      forgetStudent,
      importSubmissions,
      addForm,
      updateForm,
    }),
    [store, submitForm, replaceAnswers, forgetStudent, importSubmissions, addForm, updateForm],
  );

  return <FormsContext.Provider value={value}>{children}</FormsContext.Provider>;
}

export function useForms(): FormsContextValue {
  const ctx = useContext(FormsContext);
  if (!ctx) throw new Error("useForms must be used within FormsProvider");
  return ctx;
}

export function answersFromStudent(
  student: {
    given: string;
    family: string;
    email: string;
    school: School;
    grade: number;
    age: number;
    birthday: string;
    guardianName: string;
    guardianEmail: string;
    guardianPhone: string;
    emergencyName: string;
    emergencyPhone: string;
  },
  consents = {
    permissionSlips: true,
    medical: true,
    conduct: true,
  },
  extraAnswers: Record<string, string> = {},
  permission?: PermissionAnswers,
): FormAnswers {
  const filed = permission ? consentFlags(permission) : consents;
  return {
    given: student.given,
    family: student.family,
    email: student.email,
    school: student.school,
    grade: student.grade,
    age: student.age,
    birthday: student.birthday,
    guardianName: student.guardianName,
    guardianEmail: student.guardianEmail,
    guardianPhone: student.guardianPhone,
    emergencyName: student.emergencyName,
    emergencyPhone: student.emergencyPhone,
    ...filed,
    permission: permission ?? emptyPermission(),
    extraAnswers,
  };
}
